import { queryOptions, keepPreviousData } from '@tanstack/react-query';
import { SEARCH_STALE_TIME, SEARCH_SUGGESTIONS_STALE_TIME } from '@/constants/search';
import {
  FilingInstructionType,
  TaxBindersApi,
  BinderStage,
  BinderStatusFilter,
  TaxReturnBundleApi,
  ClientType,
  ConfidentialFilter,
  DocumentRequestsApi,
  DocumentsApi,
  ClientsApi,
  ClientWatcherApi,
  ClientNotesApi,
  CommentsApi,
  DocumentDirection,
  OptionsApi,
  ExternalClientsApi,
  PreparerDashboardApi,
  UsersApi,
  UserRole,
  ClientNotificationPreferencesApi,
  NotificationsApi,
  NotificationPreferencesApi,
  NotificationActionType,
  type TaxBinderDtoPaginatedResponseApiResponse,
  type TaxBinderDto,
  type TaxReturnBundleJurisdictionDto,
  type TaxReturnBundleFilingInstructionDto,
  type TaxReturnBundlePaymentVoucherDto,
  type BinderEstimatedPaymentsDto,
  type BundleProcessingStatusDto,
  type BundleSignatureStatusDto,
  type DocumentRequestDto,
  type DocumentDtoPaginatedResponseApiResponse,
  type DocumentType,
  type ClientAuditHistoryDto,
  type TaxBinderAuditHistoryDto,
  type TaxBinderExportPreviewDto,
  type TaxBinderExportSelectionDto,
  type ClientDto,
  type ClientDetailsDto,
  type QuestionnairePreFillDataDto,
  type ClientNoteDto,
  type ServiceLine,
  type CommentDtoPaginatedResponse,
  type CommentDto,
  type OptionDto,
  type ExternalClientDto,
  type ExternalClientSearchResultDtoPaginatedResponse,
  type ExternalClientSearchSuggestionDto,
  type DashboardStatsDto,
  type ClientSearchResultDtoPaginatedResponse,
  type ClientSearchSuggestionDto,
  type SmsConsentStatusDto,
  type ClientNotificationPreferenceDto,
  type BundleDocumentDto,
  type UnifiedNotificationDto,
  type UnifiedNotificationDtoPaginatedResponse,
  type UserNotificationPreferenceDto,
  type EnumOptionDto,
  type ActionTypeDto,
  type ActionPreferencesResponseDto,
  type CchDocumentDto,
  UserSettingsApi,
  type UserSettingDto,
  BindersApi,
  AuditBindersApi,
  AuditDashboardApi,
  type AuditBinderStage,
  AuditDocumentRequestType,
  type BinderListItemDtoPaginatedResponseApiResponse,
  type AuditBinderDto,
  type AuditDashboardItemDtoPaginatedResponseApiResponse,
} from '@ata-digital/client-tax-system-api-client';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import {
  ExternalDocumentSharesApi,
  type ExternalDocumentShareDtoPaginatedResponse,
} from '@ata-digital/client-tax-system-api-client';
import type { BinderFilters, DocumentFilters, DashboardStatsFilters } from '@/types/api';

/**
 * Default page size constants
 * API maximum page size is 200 - requests exceeding this will fail
 */
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

/**
 * Centralized query options for binders
 * Used for consistent data fetching across components and route loaders
 * Returns TaxBinderDto types from API client
 */
export const taxBinderQueryOptions = {
  /**
   * Query options for fetching paginated binders list
   * Returns TaxBinderDtoPaginatedResponseApiResponse from API client
   * @param params - Filter parameters for the binders list
   * @param token - Auth token
   * @param activeClientId - The active client ID for cache separation
   */
  list: (params: BinderFilters = {}, token?: string, activeClientId?: string) =>
    queryOptions({
      // Include activeClientId in query key so cache is client-specific
      queryKey: ['taxBinders', 'list', activeClientId, params] as const,
      queryFn: async (): Promise<TaxBinderDtoPaginatedResponseApiResponse> => {
        const bindersApi = new TaxBindersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await bindersApi.apiTaxBindersGet({
          page: params.page,
          limit: params.limit,
          clientId: params.clientId,
          taxYear: params.taxYear,
          taxType: params.taxType as ClientType | undefined,
          stage: params.stage as BinderStage | undefined,
          search: params.search,
          preparerId: params.preparerId,
          partnerId: params.partnerId,
          officeId: params.officeId,
          dueDateFrom: params.dueDateFrom,
          dueDateTo: params.dueDateTo,
          priority: params.priority,
          isOverdue: params.isOverdue,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          watchedClientsOnly: params.watchedClientsOnly,
          isClientSubmitted: params.isClientSubmitted,
          hasUnreviewedDocuments: params.hasUnreviewedDocuments,
          hasUnreviewedComments: params.hasUnreviewedComments,
          hasLateDocuments: params.hasLateDocuments,
          assignedToMe: params.assignedToMe,
        });

        return response.data as TaxBinderDtoPaginatedResponseApiResponse;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
      placeholderData: keepPreviousData, // Keep previous data visible during sort/pagination changes
    }),

  /**
   * Query options for fetching a single binder by ID
   * Returns the TaxBinderDto directly, unwrapped from the API response
   */
  detail: (id: string, token?: string) =>
    queryOptions({
      queryKey: ['taxBinders', 'detail', id] as const,
      queryFn: async (): Promise<TaxBinderDto | null> => {
        const bindersApi = new TaxBindersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await bindersApi.apiTaxBindersIdGet({ id });
        // API returns BinderDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the TaxBinderDto
        return response.data?.data || null;
      },
      enabled: !!token && !!id,
      staleTime: 1000 * 60 * 10, // 10 minutes
      retry: false, // Don't retry on 403/404 errors - let the component handle them
      throwOnError: false, // Don't throw errors to error boundary - let the component handle them
    }),

  /**
   * Query options for fetching binders by client ID
   */
  byClient: (clientId: string, token?: string) => taxBinderQueryOptions.list({ clientId }, token),

  /**
   * Query options for fetching binders by tax year
   */
  byTaxYear: (taxYear: number, token?: string) => taxBinderQueryOptions.list({ taxYear }, token),
} as const;

// Legacy query keys for backward compatibility
export const taxBinderKeys = {
  all: ['taxBinders'] as const,
  lists: () => [...taxBinderKeys.all, 'list'] as const,
  list: (filters: BinderFilters) => [...taxBinderKeys.lists(), filters] as const,
  details: () => [...taxBinderKeys.all, 'detail'] as const,
  detail: (id: string) => [...taxBinderKeys.details(), id] as const,
} as const;

export interface AuditDashboardFilters {
  clientId?: string;
  preparerId?: string;
  officeId?: string;
  stages?: AuditBinderStage[];
  dueDateStart?: string;
  dueDateEnd?: string;
  periodStart?: string;
  periodEnd?: string;
  watchedClientsOnly?: boolean;
  assignedToMe?: boolean;
  isArchived?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  requestType?: AuditDocumentRequestType;
}

export { AuditDocumentRequestType };

/**
 * Centralized query options for the unified Binders endpoint (`GET /api/binders`).
 * Returns a polymorphic list discriminated by `binderType` (Tax or Audit).
 */
export const binderListQueryOptions = {
  byClient: (clientId: string, token?: string) =>
    queryOptions({
      queryKey: ['binders', 'byClient', clientId] as const,
      queryFn: async (): Promise<BinderListItemDtoPaginatedResponseApiResponse> => {
        const api = new BindersApi(createApiConfig(token), undefined, apiAxiosInstance);
        const response = await api.apiBindersGet({ clientId });
        return response.data as BinderListItemDtoPaginatedResponseApiResponse;
      },
      enabled: !!token && !!clientId,
      staleTime: 1000 * 60 * 2,
    }),
} as const;

export const binderListKeys = {
  all: ['binders'] as const,
  byClient: (clientId: string) => [...binderListKeys.all, 'byClient', clientId] as const,
} as const;

/**
 * Centralized query options for the preparer audit dashboard
 * (`GET /api/audit-dashboard`).
 */
export const auditDashboardQueryOptions = {
  list: (params: AuditDashboardFilters = {}, token?: string, activeClientId?: string) =>
    queryOptions({
      queryKey: ['auditDashboard', 'list', activeClientId, params] as const,
      queryFn: async (): Promise<AuditDashboardItemDtoPaginatedResponseApiResponse> => {
        const api = new AuditDashboardApi(createApiConfig(token), undefined, apiAxiosInstance);
        const response = await api.apiAuditDashboardGet({
          clientId: params.clientId,
          preparerId: params.preparerId,
          officeId: params.officeId,
          stages: params.stages,
          dueDateStart: params.dueDateStart,
          dueDateEnd: params.dueDateEnd,
          periodStart: params.periodStart,
          periodEnd: params.periodEnd,
          watchedClientsOnly: params.watchedClientsOnly,
          assignedToMe: params.assignedToMe,
          isArchived: params.isArchived,
          search: params.search,
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          requestType: params.requestType,
        });
        // response.data is ApiResponse<PaginatedResponse<AuditDashboardItemDto>>
        return response.data;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5,
      placeholderData: keepPreviousData,
    }),
} as const;

export const auditDashboardKeys = {
  all: ['auditDashboard'] as const,
  lists: () => [...auditDashboardKeys.all, 'list'] as const,
  list: (filters: AuditDashboardFilters) => [...auditDashboardKeys.lists(), filters] as const,
} as const;

/**
 * Centralized query options for audit binders.
 */
export const auditBinderQueryOptions = {
  detail: (id: string, token?: string) =>
    queryOptions({
      queryKey: ['auditBinders', 'detail', id] as const,
      queryFn: async (): Promise<AuditBinderDto | null> => {
        const api = new AuditBindersApi(createApiConfig(token), undefined, apiAxiosInstance);
        const response = await api.apiAuditBindersIdGet({ id });
        return response.data?.data || null;
      },
      enabled: !!token && !!id,
      staleTime: 1000 * 60 * 10,
      retry: false,
      throwOnError: false,
    }),
} as const;

export const auditBinderKeys = {
  all: ['auditBinders'] as const,
  lists: () => [...auditBinderKeys.all, 'list'] as const,
  list: (filters: AuditDashboardFilters) => [...auditBinderKeys.lists(), filters] as const,
  detail: (id: string) => [...auditBinderKeys.all, 'detail', id] as const,
} as const;

/**
 * Centralized query options for the preparer audit binders management list
 * (`GET /api/audit-binders`).
 */
export const auditBinderListQueryOptions = {
  list: (params: AuditDashboardFilters = {}, token?: string) =>
    queryOptions({
      queryKey: auditBinderKeys.list(params),
      queryFn: async (): Promise<AuditDashboardItemDtoPaginatedResponseApiResponse> => {
        const api = new AuditBindersApi(createApiConfig(token), undefined, apiAxiosInstance);
        const response = await api.apiAuditBindersGet({
          clientId: params.clientId,
          preparerId: params.preparerId,
          officeId: params.officeId,
          stages: params.stages,
          dueDateStart: params.dueDateStart,
          dueDateEnd: params.dueDateEnd,
          periodStart: params.periodStart,
          periodEnd: params.periodEnd,
          isArchived: params.isArchived,
          search: params.search,
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        });
        return response.data;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5,
      placeholderData: keepPreviousData,
    }),
} as const;

/**
 * Centralized query options for document requests
 * Used for consistent data fetching across components and route loaders
 */
export const documentRequestQueryOptions = {
  /**
   * Query options for fetching a single document request by ID
   * Returns the DocumentRequestDto directly, unwrapped from the API response
   */
  detail: (id: string, token?: string) =>
    queryOptions({
      queryKey: ['documentRequests', 'detail', id] as const,
      queryFn: async (): Promise<DocumentRequestDto | null> => {
        const documentRequestsApi = new DocumentRequestsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await documentRequestsApi.apiDocumentRequestsIdGet({ id });
        // API returns DocumentRequestDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the DocumentRequestDto
        return response.data?.data || null;
      },
      enabled: !!token && !!id,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Legacy query keys for backward compatibility
export const documentRequestKeys = {
  all: ['documentRequests'] as const,
  details: () => [...documentRequestKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentRequestKeys.details(), id] as const,
} as const;

/**
 * Centralized query options for documents
 * Used for consistent data fetching across components and route loaders
 */
export const documentQueryOptions = {
  /**
   * Query options for fetching paginated documents list
   * Returns the full paginated response including items and pagination metadata
   * Default page size is 50, maximum is 200 (API constraint)
   */
  list: (params: DocumentFilters = {}, token?: string) =>
    queryOptions({
      queryKey: ['documents', 'list', params] as const,
      queryFn: async (): Promise<DocumentDtoPaginatedResponseApiResponse> => {
        const documentsApi = new DocumentsApi(createApiConfig(token), undefined, apiAxiosInstance);

        // Ensure limit doesn't exceed API maximum (200)
        const safeLimit = params.limit ? Math.min(params.limit, MAX_PAGE_SIZE) : DEFAULT_PAGE_SIZE;

        const response = await documentsApi.apiDocumentsGet({
          page: params.page,
          limit: safeLimit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          type: params.type as unknown as DocumentType,
          binderId: params.binderId,
          direction: params.direction as unknown as DocumentDirection,
          category: params.category,
          searchTerm: params.searchTerm, // Search by filename or description
          isExported: params.isExported,
          exportedAfter: params.exportedAfter,
          exportedBefore: params.exportedBefore,
          exportedById: params.exportedById,
        });

        return response.data as DocumentDtoPaginatedResponseApiResponse;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 2, // 2 minutes - documents change frequently
      placeholderData: keepPreviousData, // Keep previous page data visible during pagination
    }),

  /**
   * Query options for fetching documents by binder ID
   */
  byBinder: (binderId: string, params: Omit<DocumentFilters, 'binderId'> = {}, token?: string) =>
    documentQueryOptions.list({ ...params, binderId }, token),
} as const;

// Legacy query keys for backward compatibility
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (filters: DocumentFilters) => [...documentKeys.lists(), filters] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
  previews: () => [...documentKeys.all, 'preview'] as const,
  preview: (previewId: string) => [...documentKeys.previews(), previewId] as const,
} as const;

/**
 * Centralized query options for client audit history
 * Uses specialized ClientsApi endpoint that returns rich audit data
 */
export const clientAuditQueryOptions = {
  /**
   * Query options for fetching client audit history
   * Returns ClientAuditHistoryDto with detailed audit entries including descriptions and field changes
   */
  history: (clientId: string, token?: string) =>
    queryOptions({
      queryKey: ['clientAudit', 'history', clientId] as const,
      queryFn: async (): Promise<ClientAuditHistoryDto | null> => {
        const clientsApi = new ClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientsApi.apiClientsIdAuditHistoryGet({ id: clientId });
        // API returns ClientAuditHistoryDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the ClientAuditHistoryDto
        return response.data?.data || null;
      },
      enabled: !!token && !!clientId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Legacy query keys for backward compatibility
export const clientAuditKeys = {
  all: ['clientAudit'] as const,
  histories: () => [...clientAuditKeys.all, 'history'] as const,
  history: (clientId: string) => [...clientAuditKeys.histories(), clientId] as const,
} as const;

/**
 * Centralized query options for binder audit history
 * Uses specialized TaxBindersApi endpoint that returns rich audit data
 */
export const binderAuditQueryOptions = {
  /**
   * Query options for fetching binder audit history
   * Returns TaxBinderAuditHistoryDto with detailed audit entries including descriptions and field changes
   */
  history: (binderId: string, token?: string) =>
    queryOptions({
      queryKey: ['binderAudit', 'history', binderId] as const,
      queryFn: async (): Promise<TaxBinderAuditHistoryDto | null> => {
        const bindersApi = new TaxBindersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await bindersApi.apiTaxBindersIdAuditHistoryGet({ id: binderId });
        // API returns BinderAuditHistoryDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the TaxBinderAuditHistoryDto
        return response.data?.data || null;
      },
      enabled: !!token && !!binderId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Legacy query keys for backward compatibility
export const binderAuditKeys = {
  all: ['binderAudit'] as const,
  histories: () => [...binderAuditKeys.all, 'history'] as const,
  history: (binderId: string) => [...binderAuditKeys.histories(), binderId] as const,
} as const;

/**
 * Centralized query options for binder export
 * Used for fetching export preview data before downloading
 */
export const binderExportQueryOptions = {
  /**
   * Query options for fetching export preview
   * Returns TaxBinderExportPreviewDto with file counts, sizes, and recommendations
   */
  preview: (binderId: string, selection?: TaxBinderExportSelectionDto, token?: string) =>
    queryOptions({
      queryKey: ['taxBinders', 'export-preview', binderId, selection] as const,
      queryFn: async (): Promise<TaxBinderExportPreviewDto | null> => {
        const bindersApi = new TaxBindersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await bindersApi.apiTaxBindersIdExportPreviewPost({
          id: binderId,
          taxBinderExportSelectionDto: selection,
        });
        // API returns BinderExportPreviewDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the TaxBinderExportPreviewDto
        return response.data?.data || null;
      },
      enabled: !!token && !!binderId,
      staleTime: 0, // Always fetch fresh preview data
    }),
} as const;

/**
 * Centralized query options for clients
 * Used for client switching functionality and fetching available clients
 */
export const clientQueryOptions = {
  /**
   * Query options for fetching available clients for the authenticated user
   * Returns ClientDto[] with maskedTaxId field for display
   * Uses apiClientsMyClientsGet endpoint which returns clients the user has access to
   */
  availableClients: (token?: string) =>
    queryOptions({
      queryKey: ['clients', 'available'] as const,
      queryFn: async (): Promise<ClientDto[]> => {
        const clientsApi = new ClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientsApi.apiClientsMyClientsGet();
        // API returns ClientDtoListApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the ClientDto[]
        if (!response.data?.data) {
          throw new Error(
            response.data?.error?.message || 'Failed to fetch available clients: No data returned from API.'
          );
        }
        return response.data.data;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 10, // 10 minutes - clients don't change frequently
    }),

  /**
   * Query options for fetching detailed client information by ID
   * Returns ClientDetailsDto with full client details including address, contacts, statistics
   * Unwraps the nested API response structure: response.data.data contains the ClientDetailsDto
   */
  detail: (clientId: string, token?: string) =>
    queryOptions({
      queryKey: ['clients', 'detail', clientId] as const,
      queryFn: async (): Promise<ClientDetailsDto | null> => {
        const clientsApi = new ClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientsApi.apiClientsIdGet({ id: clientId });
        // API returns ClientDetailsDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the ClientDetailsDto
        return response.data?.data || null;
      },
      enabled: !!token && !!clientId,
      staleTime: 1000 * 60 * 10, // 10 minutes - client details don't change frequently
      retry: false, // Don't retry on 403/404 errors - let the component handle them
      throwOnError: false, // Don't throw errors to error boundary - let the component handle them
    }),
} as const;

// Legacy query keys for backward compatibility
export const clientKeys = {
  all: ['clients'] as const,
  available: () => [...clientKeys.all, 'available'] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (clientId: string) => [...clientKeys.details(), clientId] as const,
  search: () => [...clientKeys.all, 'search'] as const,
  searchList: (filters: ClientSearchFilters) => [...clientKeys.search(), filters] as const,
  suggestions: () => [...clientKeys.all, 'suggestions'] as const,
} as const;

/**
 * Client search filter interface for semantic search
 */
export interface ClientSearchFilters {
  searchTerm?: string;
  status?: string;
  type?: string;
  officeId?: string;
  officeIds?: string[];
  partnerId?: string; // Filter by partner (client's assigned partner)
  state?: string;
  enableSemanticSearch?: boolean;
  watchedClientsOnly?: boolean;
  binderStatus?: 'has_open' | 'all_completed' | 'no_binders';
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  confidentialFilter?: 'all' | 'publicOnly' | 'myViewableClients' | 'restricted' | 'allConfidential';
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Centralized query options for client semantic search
 * Uses ClientsApi.apiClientsSearchHybridPost for full semantic/fuzzy/exact search
 */
export const clientSearchQueryOptions = {
  /**
   * Query options for semantic client search
   * Returns ClientSearchResultDto[] with nested ClientDto and search scores
   * Unwraps the nested API response structure
   */
  search: (filters: ClientSearchFilters, token?: string) =>
    queryOptions({
      queryKey: clientKeys.searchList(filters),
      queryFn: async (): Promise<ClientSearchResultDtoPaginatedResponse> => {
        const clientsApi = new ClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        // Convert string type to enum if provided
        let typeEnum: ClientType | undefined;
        if (filters.type) {
          typeEnum = filters.type as ClientType;
        }

        // Convert binder status filter to API enum
        let binderStatusEnum: BinderStatusFilter | undefined;
        if (filters.binderStatus) {
          const statusMap: Record<string, BinderStatusFilter> = {
            has_open: BinderStatusFilter.HasOpen,
            all_completed: BinderStatusFilter.AllCompleted,
            no_binders: BinderStatusFilter.NoBinders,
          };
          binderStatusEnum = statusMap[filters.binderStatus];
        }

        // Pass confidential filter directly - no conversion needed since we're using the enum values
        let confidentialFilterEnum: ConfidentialFilter | undefined;
        if (filters.confidentialFilter) {
          confidentialFilterEnum = filters.confidentialFilter as ConfidentialFilter;
        }

        const response = await clientsApi.apiClientsSearchHybridPost({
          clientSearchQueryDto: {
            searchTerm: filters.searchTerm || '',
            type: typeEnum,
            officeId: filters.officeId,
            officeIds: filters.officeIds,
            partnerId: filters.partnerId,
            state: filters.state,
            enableSemanticSearch: filters.enableSemanticSearch ?? true,
            watchedClientsOnly: filters.watchedClientsOnly,
            binderStatus: binderStatusEnum,
            confidentialFilter: confidentialFilterEnum,
            dateRange: filters.dateRange
              ? {
                  startDate: filters.dateRange.startDate,
                  endDate: filters.dateRange.endDate,
                }
              : undefined,
            page: filters.page || 1,
            pageSize: filters.pageSize || 25,
            sortBy: filters.sortBy || 'name',
            sortOrder: filters.sortOrder || 'asc',
          },
        });

        // API returns ClientSearchResultDtoPaginatedResponseApiResponse
        // response.data.data contains the ClientSearchResultDtoPaginatedResponse
        return response.data?.data || { items: [], pagination: undefined };
      },
      enabled: !!token,
      staleTime: SEARCH_STALE_TIME, // 30s - search results should be fresh (#378)
      placeholderData: keepPreviousData,
    }),

  /**
   * Query options for client search suggestions (autocomplete)
   * Returns ClientSearchSuggestionDto[] for typeahead functionality
   */
  suggestions: (query: string, limit: number = 10, token?: string) =>
    queryOptions({
      queryKey: [...clientKeys.suggestions(), query, limit] as const,
      queryFn: async (): Promise<ClientSearchSuggestionDto[]> => {
        const clientsApi = new ClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientsApi.apiClientsSearchSuggestionsGet({
          q: query,
          limit,
        });

        // API returns ClientSearchSuggestionDtoListApiResponse
        // response.data.data contains the ClientSearchSuggestionDto[]
        return response.data?.data || [];
      },
      enabled: !!token && query.length >= 2,
      staleTime: SEARCH_SUGGESTIONS_STALE_TIME, // 30s - suggestions should be fresh (#378)
    }),
} as const;

/**
 * Centralized query options for questionnaire pre-fill data
 * Uses ClientsApi.apiClientsClientIdQuestionnairePreFillDataGet endpoint
 */
export const questionnaireQueryOptions = {
  /**
   * Query options for fetching questionnaire pre-fill data
   * Returns QuestionnairePreFillDataDto with all information needed to pre-populate the tax questionnaire
   * Unwraps the nested API response structure: response.data.data contains the QuestionnairePreFillDataDto
   *
   * @param clientId - Client ID to fetch data for
   * @param binderId - Optional binder ID to fetch data for specific binder context
   * @param token - Authentication token
   */
  preFillData: (clientId: string, binderId?: string, token?: string) =>
    queryOptions({
      queryKey: ['questionnaires', 'preFillData', clientId, binderId] as const,
      queryFn: async (): Promise<QuestionnairePreFillDataDto | null> => {
        const clientsApi = new ClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientsApi.apiClientsClientIdQuestionnairePreFillDataGet({
          clientId,
          binderId,
        });
        // API returns QuestionnairePreFillDataDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the QuestionnairePreFillDataDto
        return response.data?.data || null;
      },
      enabled: !!token && !!clientId,
      staleTime: 1000 * 60 * 10, // 10 minutes - pre-fill data doesn't change frequently
    }),
} as const;

// Legacy query keys for backward compatibility
export const questionnaireKeys = {
  all: ['questionnaires'] as const,
  preFillData: () => [...questionnaireKeys.all, 'preFillData'] as const,
  preFillDataDetail: (clientId: string, binderId?: string) =>
    binderId !== undefined
      ? ([...questionnaireKeys.preFillData(), clientId, binderId] as const)
      : ([...questionnaireKeys.preFillData(), clientId] as const),
} as const;

/**
 * Centralized query options for client watchers (follow feature)
 * Used for fetching and managing watch status for clients
 */
export const clientWatcherQueryOptions = {
  /**
   * Query options for checking if current user is watching a specific client
   * Returns boolean indicating watch status
   */
  isWatching: (clientId: string, token?: string) =>
    queryOptions({
      queryKey: ['clients', 'watchers', 'me', clientId] as const,
      queryFn: async (): Promise<boolean> => {
        const clientWatcherApi = new ClientWatcherApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientWatcherApi.apiClientsClientIdWatchersMeGet({ clientId });
        // API returns BooleanApiResponse with structure: { success?, data?, error?, meta? }
        // response.data.data contains the boolean value
        return response.data?.data || false;
      },
      enabled: !!token && !!clientId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),

  /**
   * Query options for fetching all watched client IDs for current user
   * Returns array of client IDs (GUIDs)
   */
  watchedList: (token?: string) =>
    queryOptions({
      queryKey: ['clients', 'watched'] as const,
      queryFn: async (): Promise<string[]> => {
        const clientWatcherApi = new ClientWatcherApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientWatcherApi.apiClientsWatchedGet();
        // API returns GuidListApiResponse with structure: { success?, data?, error?, meta? }
        // response.data.data contains the array of GUIDs
        return response.data?.data || [];
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Legacy query keys for backward compatibility
export const clientWatcherKeys = {
  all: ['clients', 'watchers'] as const,
  isWatching: (clientId: string) => [...clientWatcherKeys.all, 'me', clientId] as const,
  watchedList: () => ['clients', 'watched'] as const,
} as const;

/**
 * Type for comment query filters
 */
export type CommentFilters = {
  documentId?: string;
  binderId?: string;
  documentRequestId?: string;
  createdById?: string;
  createdAfter?: string;
  createdBefore?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
};

/**
 * Centralized query options for comments
 * Used for consistent data fetching across components and route loaders
 * Returns CommentDto types from API client
 */
export const commentQueryOptions = {
  /**
   * Query options for fetching paginated comments list
   * Returns CommentDtoPaginatedResponse with items and pagination metadata
   * Supports filtering by documentId, binderId, documentRequestId
   */
  list: (params: CommentFilters = {}, token?: string) =>
    queryOptions({
      queryKey: ['comments', 'list', params] as const,
      queryFn: async (): Promise<CommentDtoPaginatedResponse> => {
        const commentsApi = new CommentsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await commentsApi.apiCommentsGet({
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          documentId: params.documentId,
          binderId: params.binderId,
          documentRequestId: params.documentRequestId,
          createdById: params.createdById,
          createdAfter: params.createdAfter,
          createdBefore: params.createdBefore,
        });

        // API returns CommentDtoPaginatedResponseApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the CommentDtoPaginatedResponse
        return response.data?.data || { items: [], pagination: undefined };
      },
      enabled: !!token,
      staleTime: 1000 * 30, // 30 seconds - comments change frequently
      placeholderData: keepPreviousData, // Keep previous data visible during pagination
    }),

  /**
   * Query options for fetching a single comment by ID
   * Returns the CommentDto directly, unwrapped from the API response
   */
  detail: (id: string, token?: string) =>
    queryOptions({
      queryKey: ['comments', 'detail', id] as const,
      queryFn: async (): Promise<CommentDto | null> => {
        const commentsApi = new CommentsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await commentsApi.apiCommentsIdGet({ id });
        // API returns CommentDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the CommentDto
        return response.data?.data || null;
      },
      enabled: !!token && !!id,
      staleTime: 1000 * 60, // 1 minute
    }),

  /**
   * Query options for fetching comments for a specific document request
   * Convenience method that filters by documentRequestId
   */
  byDocumentRequest: (
    documentRequestId: string,
    params: Omit<CommentFilters, 'documentRequestId'> = {},
    token?: string
  ) => commentQueryOptions.list({ ...params, documentRequestId }, token),

  /**
   * Query options for fetching comments for a specific binder
   * Convenience method that filters by binderId
   */
  byBinder: (binderId: string, params: Omit<CommentFilters, 'binderId'> = {}, token?: string) =>
    commentQueryOptions.list({ ...params, binderId }, token),

  /**
   * Query options for fetching comments for a specific document
   * Convenience method that filters by documentId
   */
  byDocument: (documentId: string, params: Omit<CommentFilters, 'documentId'> = {}, token?: string) =>
    commentQueryOptions.list({ ...params, documentId }, token),
} as const;

// Legacy query keys for backward compatibility
export const commentKeys = {
  all: ['comments'] as const,
  lists: () => [...commentKeys.all, 'list'] as const,
  list: (filters?: CommentFilters) => [...commentKeys.lists(), filters] as const,
  details: () => [...commentKeys.all, 'detail'] as const,
  detail: (id: string) => [...commentKeys.details(), id] as const,
  documentRequestComments: (documentRequestId: string) =>
    [...commentKeys.all, 'documentRequest', documentRequestId] as const,
} as const;

/**
 * Centralized query options for client notes
 * Staff-only notes scoped to a client + service line, shared across binders
 */
export const clientNoteQueryOptions = {
  /**
   * Query options for fetching a client's notes within a service line (newest first)
   */
  list: (clientId: string, serviceLine: ServiceLine, token?: string) =>
    queryOptions({
      queryKey: ['clientNotes', 'list', clientId, serviceLine] as const,
      queryFn: async (): Promise<ClientNoteDto[]> => {
        const clientNotesApi = new ClientNotesApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await clientNotesApi.apiClientNotesGet({ clientId, serviceLine });
        // response.data.data is the ClientNoteDto array inside the ApiResponse wrapper
        return response.data?.data || [];
      },
      enabled: !!token && !!clientId,
      staleTime: 1000 * 30, // 30 seconds - notes change frequently while staff collaborate
    }),
} as const;

// Legacy query keys for backward compatibility
export const clientNoteKeys = {
  all: ['clientNotes'] as const,
  lists: () => [...clientNoteKeys.all, 'list'] as const,
  list: (clientId: string, serviceLine: ServiceLine) => [...clientNoteKeys.lists(), clientId, serviceLine] as const,
} as const;

/**
 * Filter type for external client search
 */
export type ExternalClientSearchFilters = {
  searchTerm: string;
  sourceSystem?: string;
  officeId?: string;
  clientType?: string;
  isActive?: boolean;
  isImported?: boolean;
  enableSemanticSearch?: boolean;
  page?: number;
  pageSize?: number;
};

/**
 * Centralized query options for offices (dropdown options)
 * Used for populating office selection dropdowns/filters
 */
export const officeQueryOptions = {
  /**
   * Query options for fetching all offices (authenticated)
   * Returns OptionDto[] with id, name, displayOrder, isActive
   * @param token - Auth token
   */
  list: (token?: string) =>
    queryOptions({
      queryKey: ['offices', 'list'] as const,
      queryFn: async (): Promise<OptionDto[]> => {
        const optionsApi = new OptionsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await optionsApi.apiOptionsOfficesGet();
        // API returns OptionDtoListApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the OptionDto[]
        return response.data?.data || [];
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 30, // 30 minutes - offices don't change often
    }),

  /**
   * Query options for fetching all offices (public/unauthenticated)
   * Used for public forms like access request where user is not logged in
   * Returns OptionDto[] with id, name, displayOrder, isActive
   */
  listPublic: () =>
    queryOptions({
      queryKey: ['offices', 'list', 'public'] as const,
      queryFn: async (): Promise<OptionDto[]> => {
        const optionsApi = new OptionsApi(createApiConfig(), undefined, apiAxiosInstance);

        const response = await optionsApi.apiOptionsOfficesGet();
        // API returns OptionDtoListApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the OptionDto[]
        return response.data?.data || [];
      },
      staleTime: 1000 * 60 * 30, // 30 minutes - offices don't change often
    }),
} as const;

// Legacy query keys for backward compatibility
export const officeKeys = {
  all: ['offices'] as const,
  list: () => [...officeKeys.all, 'list'] as const,
  listPublic: () => [...officeKeys.all, 'list', 'public'] as const,
} as const;

/**
 * Centralized query options for preparer dashboard stats
 * Used for consistent data fetching across components and route loaders
 * Creates a fresh API instance for each query to ensure proper parameter serialization
 */
export const dashboardStatsQueryOptions = {
  /**
   * Query options for fetching dashboard statistics with optional filters
   * Returns DashboardStatsDto with stats filtered by office, preparer, or tax year
   * Creates a fresh API instance to avoid parameter serialization issues
   */
  stats: (filters?: DashboardStatsFilters, token?: string) =>
    queryOptions({
      queryKey: ['preparerDashboard', 'stats', filters] as const,
      queryFn: async (): Promise<DashboardStatsDto | null> => {
        const dashboardApi = new PreparerDashboardApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await dashboardApi.apiPreparerDashboardStatsGet({
          officeId: filters?.officeId,
          preparerId: filters?.preparerId,
          taxYear: filters?.taxYear,
          watchedClientsOnly: filters?.watchedClientsOnly,
          assignedToMe: filters?.assignedToMe,
          stage: filters?.stage as BinderStage | undefined,
          taxType: filters?.taxType as ClientType | undefined,
          isClientSubmitted: filters?.isClientSubmitted,
        });
        // API returns DashboardStatsDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the DashboardStatsDto
        return response.data?.data || null;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Legacy query keys for backward compatibility
export const dashboardStatsKeys = {
  all: ['preparerDashboard'] as const,
  stats: () => [...dashboardStatsKeys.all, 'stats'] as const,
  statsFiltered: (filters?: DashboardStatsFilters) => [...dashboardStatsKeys.stats(), filters] as const,
} as const;

/**
 * Centralized query options for external clients
 * Used for searching and importing external clients from CCH and other source systems
 */
export const externalClientQueryOptions = {
  /**
   * Search external clients with filters
   * Semantic search is always enabled per requirements
   * Returns ExternalClientSearchResultDtoPaginatedResponse with items and pagination
   */
  search: (filters: ExternalClientSearchFilters, token?: string) =>
    queryOptions({
      queryKey: ['externalClients', 'search', filters] as const,
      queryFn: async (): Promise<ExternalClientSearchResultDtoPaginatedResponse> => {
        const api = new ExternalClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiClientsExternalSearchGet({
          searchTerm: filters.searchTerm,
          sourceSystem: filters.sourceSystem,
          officeName: filters.officeId, // API uses officeName parameter
          clientType: filters.clientType,
          isActive: filters.isActive,
          isImported: filters.isImported,
          enableSemanticSearch: true, // Always enabled per requirements
          page: filters.page,
          pageSize: filters.pageSize,
        });
        // API returns ExternalClientSearchResultDtoPaginatedResponseApiResponse
        // with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the paginated response
        return response.data?.data || { items: [], pagination: undefined };
      },
      enabled: !!token && filters.searchTerm.length >= 2,
      staleTime: SEARCH_STALE_TIME, // 30s - search results should be fresh (#378)
      // Note: NOT using keepPreviousData here - for search, users expect fresh results
      // when the query changes, not a flash of stale data before new results appear
    }),

  /**
   * Autocomplete suggestions for external client search
   * Returns ExternalClientSearchSuggestionDto[] for quick type-ahead suggestions
   */
  suggestions: (query: string, limit: number = 10, token?: string) =>
    queryOptions({
      queryKey: ['externalClients', 'suggestions', query, limit] as const,
      queryFn: async (): Promise<ExternalClientSearchSuggestionDto[]> => {
        const api = new ExternalClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiClientsExternalSuggestionsGet({ q: query, limit });
        // API returns ExternalClientSearchSuggestionDtoListApiResponse
        // with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the suggestions array
        return response.data?.data || [];
      },
      enabled: !!token && query.length >= 2,
      staleTime: SEARCH_SUGGESTIONS_STALE_TIME, // 30s - suggestions should be fresh (#378)
    }),

  /**
   * Get single external client detail by ID
   * Returns ExternalClientDto with full client details
   */
  detail: (id: string, token?: string) =>
    queryOptions({
      queryKey: ['externalClients', 'detail', id] as const,
      queryFn: async (): Promise<ExternalClientDto | null> => {
        const api = new ExternalClientsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiClientsExternalIdGet({ id });
        // API returns ExternalClientDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the ExternalClientDto
        return response.data?.data || null;
      },
      enabled: !!token && !!id,
      staleTime: 1000 * 60 * 5, // 5 minutes - client details are relatively stable
    }),
} as const;

// Legacy query keys for backward compatibility
export const externalClientKeys = {
  all: ['externalClients'] as const,
  searches: () => [...externalClientKeys.all, 'search'] as const,
  search: (filters: ExternalClientSearchFilters) => [...externalClientKeys.searches(), filters] as const,
  suggestions: () => [...externalClientKeys.all, 'suggestions'] as const,
  details: () => [...externalClientKeys.all, 'detail'] as const,
  detail: (id: string) => [...externalClientKeys.details(), id] as const,
} as const;

/**
 * Centralized query options for SMS consent
 * Used for managing user SMS consent status (A2P 10DLC compliance)
 */
export const smsConsentQueryOptions = {
  /**
   * Query options for fetching current user's SMS consent status
   * Returns SmsConsentStatusDto with consent details and phone verification status
   */
  status: (token?: string) =>
    queryOptions({
      queryKey: ['smsConsent', 'status'] as const,
      queryFn: async (): Promise<SmsConsentStatusDto | null> => {
        const usersApi = new UsersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await usersApi.apiUsersSmsConsentGet();
        // API returns SmsConsentStatusDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the SmsConsentStatusDto
        return response.data?.data || null;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Legacy query keys for backward compatibility
export const smsConsentKeys = {
  all: ['smsConsent'] as const,
  status: () => [...smsConsentKeys.all, 'status'] as const,
} as const;

/**
 * Centralized query options for client notification preferences
 * Used for managing client notification settings
 * Note: Legacy event endpoints have been removed - use unified notification system instead
 */
export const clientNotificationQueryOptions = {
  /**
   * Query options for fetching client notification preferences
   * Returns ClientNotificationPreferenceDto with all preference settings
   */
  preferences: (clientId: string, token?: string) =>
    queryOptions({
      queryKey: ['clientNotifications', 'preferences', clientId] as const,
      queryFn: async (): Promise<ClientNotificationPreferenceDto | null> => {
        const api = new ClientNotificationPreferencesApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiClientsClientIdNotificationPreferencesGet({ clientId });
        // API returns ClientNotificationPreferenceDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the ClientNotificationPreferenceDto
        return response.data?.data || null;
      },
      enabled: !!token && !!clientId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Query keys for client notification preferences
export const clientNotificationKeys = {
  all: ['clientNotifications'] as const,
  preferences: (clientId: string) => [...clientNotificationKeys.all, 'preferences', clientId] as const,
} as const;

/**
 * Type for user admin search filters
 */
export type UserAdminSearchFilters = {
  search?: string;
  role?: UserRole | number;
  isActive?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  lastLoginAfter?: string;
  lastLoginBefore?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
};

/**
 * Centralized query options for user admin search
 * Used for the user management page with full search, filter, sort, and pagination
 */
export const userAdminSearchQueryOptions = {
  /**
   * Query options for paginated user admin search
   * Returns UserAdminSearchResultDtoPaginatedResponse with users and their client assignments
   */
  search: (filters: UserAdminSearchFilters, token?: string) =>
    queryOptions({
      queryKey: userKeys.searchList(filters),
      queryFn: async () => {
        const usersApi = new UsersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await usersApi.apiUsersSearchAdminPost({
          userAdminSearchQueryDto: {
            search: filters.search || undefined,
            role: filters.role !== undefined ? (filters.role as UserRole) : undefined,
            isActive: filters.isActive,
            createdAfter: filters.createdAfter,
            createdBefore: filters.createdBefore,
            lastLoginAfter: filters.lastLoginAfter,
            lastLoginBefore: filters.lastLoginBefore,
            sortBy: filters.sortBy || 'createdAt',
            sortOrder: filters.sortOrder || 'desc',
            page: filters.page || 1,
            pageSize: filters.pageSize || 25,
          },
        });

        // API returns UserAdminSearchResultDtoPaginatedResponseApiResponse
        // response.data.data contains the UserAdminSearchResultDtoPaginatedResponse
        return response.data?.data || { items: [], pagination: undefined };
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 2, // 2 minutes
      placeholderData: keepPreviousData,
    }),

  /**
   * Query options for fetching a single user by ID with full details
   * Returns UserDto with all user information
   */
  detail: (userId: string, token?: string) =>
    queryOptions({
      queryKey: userKeys.detail(userId),
      queryFn: async () => {
        const usersApi = new UsersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await usersApi.apiUsersIdGet({ id: userId });
        // API returns UserDtoApiResponse with structure: { success?, data?, error?, meta? }
        return response.data?.data || null;
      },
      enabled: !!token && !!userId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Query keys for users
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  searchList: (filters: UserAdminSearchFilters) => [...userKeys.lists(), 'search', filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (userId: string) => [...userKeys.details(), userId] as const,
} as const;

/**
 * Centralized query options for binder tax returns (jurisdictions)
 * Used for fetching and managing tax returns within binders
 * Returns types from API client: TaxReturnBundleJurisdictionDto, BundleProcessingStatusDto, etc.
 */
export const taxReturnQueryOptions = {
  /**
   * Query options for fetching all tax returns (jurisdictions) for a binder
   * Returns TaxReturnBundleJurisdictionDto[] unwrapped from API response
   */
  list: (binderId: string, token?: string) =>
    queryOptions({
      queryKey: ['taxReturns', 'list', binderId] as const,
      queryFn: async (): Promise<TaxReturnBundleJurisdictionDto[]> => {
        const api = new TaxReturnBundleApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdReturnsGet({ binderId });
        // API returns TaxReturnBundleJurisdictionDtoListApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the TaxReturnBundleJurisdictionDto[]
        return response.data?.data || [];
      },
      enabled: !!token && !!binderId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),

  /**
   * Query options for fetching a single tax return (jurisdiction) by ID
   * Returns TaxReturnBundleJurisdictionDto unwrapped from API response
   */
  detail: (binderId: string, returnId: string, token?: string) =>
    queryOptions({
      queryKey: ['taxReturns', 'detail', binderId, returnId] as const,
      queryFn: async (): Promise<TaxReturnBundleJurisdictionDto | null> => {
        const api = new TaxReturnBundleApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdReturnsReturnIdGet({ binderId, returnId });
        // API returns TaxReturnBundleJurisdictionDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the TaxReturnBundleJurisdictionDto
        return response.data?.data || null;
      },
      enabled: !!token && !!binderId && !!returnId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),

  /**
   * Query options for fetching bundle processing status
   * Used for polling during bundle upload processing
   * Returns BundleProcessingStatusDto with status, return count, and any errors
   */
  processingStatus: (binderId: string, token?: string) =>
    queryOptions({
      queryKey: ['taxReturns', 'processingStatus', binderId] as const,
      queryFn: async (): Promise<BundleProcessingStatusDto | null> => {
        const api = new TaxReturnBundleApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdReturnsProcessingStatusGet({ binderId });
        // API returns BundleProcessingStatusDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the BundleProcessingStatusDto
        return response.data?.data || null;
      },
      enabled: !!token && !!binderId,
      staleTime: 0, // Always fetch fresh - this is for polling
    }),

  /**
   * Query options for fetching filing instructions for a specific return
   * Returns TaxReturnBundleFilingInstructionDto with amounts, due dates, addresses
   * @param instructionType - Optional instruction type (TaxReturn or EstimatedTax). Defaults to TaxReturn.
   */
  filingInstructions: (binderId: string, returnId: string, token?: string, instructionType?: FilingInstructionType) =>
    queryOptions({
      queryKey: [
        'taxReturns',
        'filingInstructions',
        binderId,
        returnId,
        instructionType ?? FilingInstructionType.TaxReturn,
      ] as const,
      queryFn: async (): Promise<TaxReturnBundleFilingInstructionDto | null> => {
        const api = new TaxReturnBundleApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdReturnsReturnIdFilingInstructionsGet({
          binderId,
          returnId,
          instructionType,
        });
        // API returns TaxReturnBundleFilingInstructionDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the TaxReturnBundleFilingInstructionDto
        return response.data?.data || null;
      },
      enabled: !!token && !!binderId && !!returnId,
      staleTime: 1000 * 60 * 10, // 10 minutes - filing instructions don't change often
    }),

  /**
   * Query options for fetching payment vouchers for a specific return
   * Returns TaxReturnBundlePaymentVoucherDto[] with Q1-Q4 voucher details
   */
  vouchers: (binderId: string, returnId: string, token?: string) =>
    queryOptions({
      queryKey: ['taxReturns', 'vouchers', binderId, returnId] as const,
      queryFn: async (): Promise<TaxReturnBundlePaymentVoucherDto[]> => {
        const api = new TaxReturnBundleApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdReturnsReturnIdVouchersGet({ binderId, returnId });
        // API returns TaxReturnBundlePaymentVoucherDtoListApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the TaxReturnBundlePaymentVoucherDto[]
        return response.data?.data || [];
      },
      enabled: !!token && !!binderId && !!returnId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),

  /**
   * Query options for fetching bundle-level signature status
   * Returns BundleSignatureStatusDto with jurisdiction and signer breakdowns
   */
  bundleSignatureStatus: (binderId: string, token?: string) =>
    queryOptions({
      queryKey: ['taxReturns', 'bundleSignatureStatus', binderId] as const,
      queryFn: async (): Promise<BundleSignatureStatusDto | null> => {
        const api = new TaxReturnBundleApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdReturnsBundleSignatureStatusGet({ binderId });
        // API returns BundleSignatureStatusDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the BundleSignatureStatusDto
        return response.data?.data || null;
      },
      enabled: !!token && !!binderId,
      staleTime: 1000 * 60 * 2, // 2 minutes - signature status can change
    }),
} as const;

// Legacy query keys for backward compatibility
export const taxReturnKeys = {
  all: ['taxReturns'] as const,
  lists: () => [...taxReturnKeys.all, 'list'] as const,
  list: (binderId: string) => [...taxReturnKeys.lists(), binderId] as const,
  details: () => [...taxReturnKeys.all, 'detail'] as const,
  detail: (binderId: string, returnId: string) => [...taxReturnKeys.details(), binderId, returnId] as const,
  processingStatus: (binderId: string) => [...taxReturnKeys.all, 'processingStatus', binderId] as const,
  filingInstructions: (binderId: string, returnId: string, instructionType?: FilingInstructionType) =>
    [
      ...taxReturnKeys.all,
      'filingInstructions',
      binderId,
      returnId,
      instructionType ?? FilingInstructionType.TaxReturn,
    ] as const,
  vouchers: (binderId: string, returnId: string) => [...taxReturnKeys.all, 'vouchers', binderId, returnId] as const,
  bundleSignatureStatus: (binderId: string) => [...taxReturnKeys.all, 'bundleSignatureStatus', binderId] as const,
} as const;

/**
 * Centralized query options for a binder's estimated tax payments.
 * Resolved from the prior-year binder's return bundle; used by the
 * current-year view's payment checklist.
 */
export const binderEstimateQueryOptions = {
  /**
   * Query options for fetching the estimated payments that apply to a binder's tax year
   * Returns BinderEstimatedPaymentsDto unwrapped from API response
   */
  list: (binderId: string, token?: string) =>
    queryOptions({
      queryKey: ['taxBinders', 'estimates', binderId] as const,
      queryFn: async (): Promise<BinderEstimatedPaymentsDto | null> => {
        const api = new TaxBindersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdEstimatedPaymentsGet({ binderId });
        // API returns BinderEstimatedPaymentsDtoApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the BinderEstimatedPaymentsDto
        return response.data?.data || null;
      },
      enabled: !!token && !!binderId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Legacy query keys for backward compatibility
export const binderEstimateKeys = {
  all: ['taxBinders', 'estimates'] as const,
  list: (binderId: string) => [...binderEstimateKeys.all, binderId] as const,
} as const;

/**
 * Centralized query options for bundle documents
 * Used for fetching structured document records within a tax return bundle
 * Returns BundleDocumentDto[] from API client
 */
export const bundleDocumentQueryOptions = {
  /**
   * Query options for fetching all bundle documents for a binder
   * Returns BundleDocumentDto[] unwrapped from API response
   */
  list: (binderId: string, token?: string) =>
    queryOptions({
      queryKey: ['bundleDocuments', 'list', binderId] as const,
      queryFn: async (): Promise<BundleDocumentDto[]> => {
        const api = new TaxReturnBundleApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersBinderIdReturnsBundleDocumentsGet({ binderId });
        // API returns BundleDocumentDtoListApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the BundleDocumentDto[]
        return response.data?.data || [];
      },
      enabled: !!token && !!binderId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Query keys for bundle documents
export const bundleDocumentKeys = {
  all: ['bundleDocuments'] as const,
  lists: () => [...bundleDocumentKeys.all, 'list'] as const,
  list: (binderId: string) => [...bundleDocumentKeys.lists(), binderId] as const,
} as const;

/**
 * Type for unified notification filters
 */
export type UnifiedNotificationFilters = {
  clientId?: string;
  /** Show notifications received by users of this client (the client's inbox view) */
  recipientClientId?: string;
  binderId?: string;
  actionType?: NotificationActionType;
  isRead?: boolean;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  pageSize?: number;
};

/**
 * Centralized query options for unified notifications
 * Used for the preparer notification center and notification bell
 */
export const unifiedNotificationQueryOptions = {
  /**
   * Query options for fetching paginated unified notifications
   * Returns UnifiedNotificationDtoPaginatedResponse with notifications for the current user
   */
  list: (filters: UnifiedNotificationFilters = {}, token?: string) =>
    queryOptions({
      queryKey: ['unifiedNotifications', 'list', filters] as const,
      queryFn: async (): Promise<UnifiedNotificationDtoPaginatedResponse> => {
        const api = new NotificationsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiNotificationsGet({
          clientId: filters.clientId,
          recipientClientId: filters.recipientClientId,
          binderId: filters.binderId,
          actionType: filters.actionType,
          isRead: filters.isRead,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          page: filters.page,
          pageSize: filters.pageSize,
        });

        return response.data?.data || { items: [], pagination: undefined };
      },
      enabled: !!token,
      staleTime: 1000 * 30, // 30 seconds - notifications should refresh frequently
      placeholderData: keepPreviousData,
    }),

  /**
   * Query options for fetching recent notifications (for bell dropdown)
   */
  recent: (limit: number = 5, token?: string) =>
    queryOptions({
      queryKey: ['unifiedNotifications', 'recent', limit] as const,
      queryFn: async (): Promise<UnifiedNotificationDto[]> => {
        const api = new NotificationsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiNotificationsRecentGet({ limit });
        return response.data?.data || [];
      },
      enabled: !!token,
      staleTime: 1000 * 15, // 15 seconds - recent notifications should be very fresh
    }),

  /**
   * Query options for fetching unread notification count (for badge)
   */
  unreadCount: (token?: string) =>
    queryOptions({
      queryKey: ['unifiedNotifications', 'unreadCount'] as const,
      queryFn: async () => {
        const api = new NotificationsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiNotificationsUnreadCountGet();
        return response.data?.data || { totalUnread: 0, highPriority: 0, byActionType: {} };
      },
      enabled: !!token,
      staleTime: 1000 * 15, // 15 seconds
      refetchInterval: 1000 * 60, // Refetch every minute for badge updates
    }),

  /**
   * Query options for fetching available action types (for filters)
   */
  actionTypes: (token?: string) =>
    queryOptions({
      queryKey: ['unifiedNotifications', 'actionTypes'] as const,
      queryFn: async (): Promise<EnumOptionDto[]> => {
        const api = new NotificationsApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiNotificationsActionTypesGet();
        return response.data?.data || [];
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 60, // 1 hour - action types rarely change
    }),
} as const;

// Query keys for unified notifications
export const unifiedNotificationKeys = {
  all: ['unifiedNotifications'] as const,
  lists: () => [...unifiedNotificationKeys.all, 'list'] as const,
  list: (filters: UnifiedNotificationFilters) => [...unifiedNotificationKeys.lists(), filters] as const,
  recent: (limit?: number) => [...unifiedNotificationKeys.all, 'recent', limit] as const,
  unreadCount: () => [...unifiedNotificationKeys.all, 'unreadCount'] as const,
  actionTypes: () => [...unifiedNotificationKeys.all, 'actionTypes'] as const,
} as const;

/**
 * Centralized query options for user notification preferences
 * Used for the preparer notification settings page
 */
export const userNotificationPreferenceQueryOptions = {
  /**
   * Query options for fetching user notification preferences
   * Returns UserNotificationPreferenceDto with all preference settings
   */
  preferences: (token?: string) =>
    queryOptions({
      queryKey: ['userNotificationPreferences', 'current'] as const,
      queryFn: async (): Promise<UserNotificationPreferenceDto | null> => {
        const api = new NotificationPreferencesApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiNotificationPreferencesGet();
        return response.data?.data || null;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),

  /**
   * Query options for fetching available action types with descriptions
   */
  actionTypes: (token?: string) =>
    queryOptions({
      queryKey: ['userNotificationPreferences', 'actionTypes'] as const,
      queryFn: async (): Promise<ActionTypeDto[]> => {
        const api = new NotificationPreferencesApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiNotificationPreferencesActionTypesGet();
        return response.data?.data || [];
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 60, // 1 hour - action types rarely change
    }),

  /**
   * Query options for fetching all action preferences
   */
  actionPreferences: (token?: string) =>
    queryOptions({
      queryKey: ['userNotificationPreferences', 'actionPreferences'] as const,
      queryFn: async (): Promise<ActionPreferencesResponseDto | null> => {
        const api = new NotificationPreferencesApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiNotificationPreferencesActionsGet();
        return response.data?.data || null;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),
} as const;

// Query keys for user notification preferences
export const userNotificationPreferenceKeys = {
  all: ['userNotificationPreferences'] as const,
  preferences: () => [...userNotificationPreferenceKeys.all, 'current'] as const,
  actionTypes: () => [...userNotificationPreferenceKeys.all, 'actionTypes'] as const,
  actionPreferences: () => [...userNotificationPreferenceKeys.all, 'actionPreferences'] as const,
} as const;

/**
 * Filter type for CCH document browsing
 */
export type CchDocumentFilters = {
  className?: string;
  subClassName?: string;
};

/**
 * Centralized query options for CCH documents
 * Used for browsing and importing documents from CCH Axcess into a binder
 */
export const cchDocumentQueryOptions = {
  /**
   * Query options for fetching available CCH documents for a binder
   * Returns CchDocumentDto[] unwrapped from API response
   * Supports server-side filtering by className and subClassName
   */
  list: (binderId: string, filters: CchDocumentFilters = {}, token?: string) =>
    queryOptions({
      queryKey: ['cchDocuments', 'list', binderId, filters] as const,
      queryFn: async (): Promise<CchDocumentDto[]> => {
        const api = new TaxBindersApi(createApiConfig(token), undefined, apiAxiosInstance);

        const response = await api.apiTaxBindersIdCchDocumentsGet({
          id: binderId,
          className: filters.className,
          subClassName: filters.subClassName,
        });
        // API returns CchDocumentDtoListApiResponse with structure: { success?, data?, error?, meta? }
        // We unwrap and return just the data field which contains the CchDocumentDto[]
        return response.data?.data || [];
      },
      enabled: !!token && !!binderId,
      staleTime: 1000 * 60 * 2, // 2 minutes - CCH documents can change
    }),
} as const;

// Legacy query keys for backward compatibility
export const cchDocumentKeys = {
  all: ['cchDocuments'] as const,
  lists: () => [...cchDocumentKeys.all, 'list'] as const,
  list: (binderId: string, filters?: CchDocumentFilters) => [...cchDocumentKeys.lists(), binderId, filters] as const,
} as const;

/**
 * Centralized query options for user settings (saved filter/sort defaults)
 */
export const userSettingQueryOptions = {
  /**
   * Query options for fetching user settings
   * Returns UserSettingDto with page defaults
   */
  current: (token?: string) =>
    queryOptions({
      queryKey: ['userSettings', 'current'] as const,
      queryFn: async (): Promise<UserSettingDto | null> => {
        const api = new UserSettingsApi(createApiConfig(token), undefined, apiAxiosInstance);
        const response = await api.apiUserSettingsGet();
        return response.data?.data || null;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 30, // 30 minutes - settings change infrequently
    }),
} as const;

// Query keys for user settings
export const userSettingKeys = {
  all: ['userSettings'] as const,
  current: () => [...userSettingKeys.all, 'current'] as const,
} as const;

/**
 * Centralized query options for external document shares
 * (documents a preparer/admin has sent to external, non-app recipients).
 */
export const externalDocumentShareQueryOptions = {
  /**
   * Query options for listing the current user's sent documents (paginated).
   * Unwrapping: response.data is ApiResponse, response.data.data is the paginated response.
   */
  list: (params: { page?: number; pageSize?: number } = {}, token?: string) =>
    queryOptions({
      queryKey: ['externalDocumentShares', 'list', params] as const,
      queryFn: async (): Promise<ExternalDocumentShareDtoPaginatedResponse | null> => {
        const api = new ExternalDocumentSharesApi(createApiConfig(token), undefined, apiAxiosInstance);
        const response = await api.apiExternalDocumentSharesGet({ page: params.page, pageSize: params.pageSize });
        return response.data?.data || null;
      },
      enabled: !!token,
      staleTime: 1000 * 60 * 2, // 2 minutes - download status can change
    }),
} as const;

// Legacy query keys for backward compatibility
export const externalDocumentShareKeys = {
  all: ['externalDocumentShares'] as const,
  lists: () => [...externalDocumentShareKeys.all, 'list'] as const,
} as const;
